import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { verifyAccount, resendVerificationCode } from '@/store/slices/authSlice';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Clock, Mail, Phone } from 'lucide-react';

const VerificationCode = ({ user, method }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(60);
  const inputs = useRef([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const focusInput = (index) => {
    if (inputs.current[index]) {
      inputs.current[index].focus();
    }
  };

  const handleCodeChange = (value, index) => {
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      focusInput(index + 1);
    }

    // Auto-submit if all digits are entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').split('').slice(0, 6);
    
    if (digits.length === 6) {
      const newCode = [...code];
      digits.forEach((digit, index) => {
        newCode[index] = digit;
      });
      setCode(newCode);
      handleSubmit(newCode.join(''));
    }
  };

  const handleSubmit = async (verificationCode = code.join('')) => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await dispatch(verifyAccount(verificationCode)).unwrap();
    } catch (err) {
      setError(err.message || 'Verification failed');
      setCode(['', '', '', '', '', '']);
      focusInput(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError(null);

    try {
      await dispatch(resendVerificationCode()).unwrap();
      setCountdown(60);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Enter Verification Code</CardTitle>
        <CardDescription>
          Enter the 6-digit code sent to your {method === 'email' ? 'email' : 'phone'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          <div className="flex justify-center space-x-2">
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(e.target.value, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-12 text-center text-xl"
                disabled={loading}
              />
            ))}
          </div>

          <Button
            type="button"
            className="w-full"
            onClick={() => handleSubmit()}
            disabled={loading || code.some(digit => digit === '')}
          >
            {loading ? 'Verifying...' : 'Verify Account'}
          </Button>

          <div className="text-center">
            <div className="flex items-center justify-center text-sm text-muted-foreground mb-2">
              <Clock className="h-4 w-4 mr-1" />
              {countdown > 0 ? `Resend code in ${countdown}s` : 'Didn\'t receive the code?'}
            </div>
            
            <Button
              type="button"
              variant="link"
              onClick={handleResendCode}
              disabled={resendLoading || countdown > 0}
              className="h-auto p-0"
            >
              {resendLoading ? 'Sending...' : 'Resend Verification Code'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VerificationCode;